import React from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { PDFCanvasProps } from './types';

export const PDFCanvas: React.FC<PDFCanvasProps> = ({
  canvasRef,
  displayWidth,
  displayHeight,
  isDragging,
  currentPage,
  pageImageUrl,
  imageLoading,
  currentPagePlaceholders,
  selectedPlaceholderId,
  dragPlaceholderId,
  popupState,
  currentPageSize,
  scale,
  onCanvasClick,
  onPlaceholderMouseDown,
  getPlaceholderScreenPos,
  getColumnDisplayName,
  selectPlaceholder,
}) => {
  return (
    <Paper
      sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2,
        backgroundColor: '#f5f5f5',
        position: 'relative',
      }}
    >
      <Box
        ref={canvasRef}
        sx={{
          position: 'relative',
          width: displayWidth,
          height: displayHeight,
          cursor: isDragging ? 'grabbing' : 'crosshair',
          userSelect: 'none',
        }}
        onClick={onCanvasClick}
      >
        {imageLoading ? (
          <Box sx={{
            width: displayWidth,
            height: displayHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
          }}>
            <CircularProgress />
          </Box>
        ) : pageImageUrl ? (
          <img
            src={pageImageUrl}
            alt={`Page ${currentPage}`}
            style={{
              width: displayWidth,
              height: displayHeight,
              pointerEvents: 'none',
            }}
            draggable={false}
          />
        ) : (
          <Box sx={{
            width: displayWidth,
            height: displayHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
          }}>
            <Typography color="text.secondary">Failed to load page</Typography>
          </Box>
        )}

        {currentPagePlaceholders.map(p => {
          const screenPos = getPlaceholderScreenPos(p);
          const isSelected = selectedPlaceholderId === p.id;
          const isDraggingThis = isDragging && dragPlaceholderId === p.id;
          const displayText = p.type === 'custom_variable'
            ? (p.custom_variable_name || 'Custom Variable')
            : p.type === 'static_text'
              ? (p.static_value || 'Static Text')
              : (p.currentValue || getColumnDisplayName(p.db_column));
          const minFontSize = Math.max(10, p.style.font_size * scale);

          return (
            <Box
              key={p.id}
              sx={{
                position: 'absolute',
                left: screenPos.x,
                top: screenPos.y,
                minWidth: p.rect.w * scale,
                minHeight: p.rect.h * scale,
                border: isSelected ? '1px solid #1976d2' : 'none',
                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                cursor: isDraggingThis ? 'grabbing' : 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'visible',
                zIndex: isSelected ? 10 : 1,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.05)',
                },
              }}
              onMouseDown={(e) => onPlaceholderMouseDown(e, p.id)}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDragging) selectPlaceholder(p.id);
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Courier New, monospace',
                  fontSize: `${minFontSize}px`,
                  color: '#000000',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayText}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {popupState && !isDragging && (
        <Box
          sx={{
            position: 'absolute',
            left: (popupState.pdfPosition.x * scale) - 8,
            top: ((currentPageSize.height - popupState.pdfPosition.y) * scale) - 8,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            border: '2px solid white',
            boxShadow: 2,
            zIndex: 20,
            pointerEvents: 'none',
          }}
        />
      )}
    </Paper>
  );
};
